import {useEffect, useState} from 'react';
import { AnimatePresence, motion } from 'framer-motion';

//components
import ToolTipWrapper from '../Tooltip/ToolTipWrapper';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { BasicCheckbox } from 'thrive-components';

//helpers
import { contentTypeImage, contentTypeString } from  '@/helpers/Archive';
import { BuildPath } from  '@/helpers/Paths';
import { Truncate } from  '@/helpers/Text';

//interfaces
import { IExistingItem } from  '@/interfaces/insightsp2';
import { archiveItem, currApp, eStatus } from  '@/interfaces/archive';


export interface IAside {
    status:eStatus, 
    selectedItem?: archiveItem, 
    contentItem:IExistingItem, 
    selectNewLocation:boolean, 
    handleCheckNewLocation:(e:any) => void, 
    draft:boolean, 
    setDraft:(val:boolean) => void, 
    restore:() => void, app:currApp
}

const variants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
};

const Aside = ({status, selectedItem, contentItem, selectNewLocation, handleCheckNewLocation, draft, setDraft, restore, app}: IAside) => {

    const [locationDisabled, setLocationDisabled] = useState<boolean>(false);
    
    useEffect(() => {
        // If this item does not have a parent - parentIsAvailable then it must be checked and user must select new location
        if (!selectedItem?.parentIsAvailable){
            handleCheckNewLocation(null)
            setLocationDisabled(true)
        }
        else{
            setLocationDisabled(false)
        }
    },[selectedItem])

    return (
        <AnimatePresence mode="wait">
            {status === eStatus.noItem && (
                <motion.div
                    key="no-item"
                    className="empty"
                    variants={variants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.5 }}
                >
                    Nothing selected
                </motion.div>
            )}
            {status === eStatus.selectedItem && (
                <motion.div
                    key="selected-item"
                    className="selected"
                    variants={variants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.5 }}
                >
                    {selectedItem &&
                        <>
                            <div className={selectedItem.featuredImageUrl ? 'icon featured-image ': 'icon content-type'}>
                                <img src={selectedItem.featuredImageUrl ? selectedItem.featuredImageUrl : `${BuildPath()}${contentTypeImage(selectedItem.contentType)}`} alt="" />
                            </div>

                            <ul>
                                <li>
                                    <h4>Name</h4>
                                    <p>{selectedItem.title}</p>
                                </li>

                                <li>
                                    <h4>TYPE</h4>
                                    <p>{contentTypeString(selectedItem.contentType)}</p>
                                </li>

                                <li>
                                    <h4>ARCHIVED BY</h4>
                                    <p>{selectedItem.archivedByDisplayName ?? 'N/A'} {new Date(selectedItem.archivedAt).toLocaleDateString().replace(/\b0/g, '')}</p>
                                </li>

                                <li>
                                    {selectedItem.firstPublishedAt
                                        ? <>
                                            <h4>ORIGINALLY PUBLISHED BY</h4>
                                            <p>{selectedItem.publishedByDisplayName ?? 'N/A'} {new Date(selectedItem.firstPublishedAt).toLocaleDateString().replace(/\b0/g, '')}</p>
                                        </>
                                        : <>
                                            <h4>Draft Created by</h4>
                                            <p>{selectedItem.createdByDisplayName ?? 'N/A'} {new Date(selectedItem.createdAt).toLocaleDateString().replace(/\b0/g, '')}</p>
                                        </>
                                    }
                                </li>

                                <li>
                                    <h4>LAST EDITED BY</h4>
                                    <p>{selectedItem.lastModifiedByDisplayName ?? 'N/A'}  {new Date(selectedItem.lastModifiedAt).toLocaleDateString().replace(/\b0/g, '')}</p>
                                </li>

                                <li>
                                    <div className='tool-tip-container'>
                                        <h4>TOTAL CONTENT ITEMS</h4>
                                        <ToolTipWrapper id="tt-content-items" className="tt-content-items" >
                                            <ReactTooltip place="top" className='thrive-tooltip' delayHide={1000} id="tt-content-items">
                                                <span>This list has items within it that will also be restored.</span>
                                            </ReactTooltip>
                                        </ToolTipWrapper>
                                    </div>

                                    <p>{selectedItem.countOfSubItems}</p>
                                </li>
                            </ul>

                            <div className='controls'>
                                <div className='options-controls'>
                                    <BasicCheckbox
                                        id={'select-new-location-checkbox'}
                                        isChecked={selectNewLocation}
                                        onChange={handleCheckNewLocation}
                                        label={'Select a new location'}
                                        isDisabled={locationDisabled}
                                        ariaLabel={'Select a new location'}
                                    />

                                    {!!selectedItem.firstPublishedAt &&
                                        <BasicCheckbox
                                            id={'restore-as-draft-checkbox'}
                                            isChecked={draft}
                                            onChange={() => setDraft(!draft)}
                                            label={'Restore as draft'}
                                            ariaLabel={'Restore as draft'}
                                        />
                                    }
                                </div>

                                <button
                                    onClick={restore}
                                    className='archive-btn search-btn'
                                    aria-label='Restore Content'
                                >
                                    Restore
                                </button>
                            </div>
                        </>
                    }
                </motion.div>
            )}
            {status === eStatus.uploading && (
                <motion.div
                    key="uploading"
                    className="uploading"
                    variants={variants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.5 }}
                >
                    {selectedItem && app && (
                        <>
                            <div className={selectedItem.featuredImageUrl ? 'icon featured-image ': 'icon content-type'}>
                                <img src={selectedItem.featuredImageUrl ? selectedItem.featuredImageUrl : `${BuildPath()}${contentTypeImage(selectedItem.contentType)}`} alt="" />
                            </div>

                            <p>‘{Truncate(selectedItem.title, 80)}’ successfully  {contentItem ? <span>moved into ‘{Truncate(contentItem.title, 30)}’</span> : 'restored'}</p>

                            <a href={`/a/${app.appGuid}/Content/Index/${selectedItem.id}`}>
                                Go to ‘{Truncate(selectedItem.title, 30)}’
                            </a>
                        </>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    )    
};

export default Aside;